import { API_URL } from '../constants';
import { resolveAccessToken } from './Auth';
import { CLIENT_DEMO_VIEW } from '@common/synapse/client-demo-sanitize';
import type {
  AddManualClaimRequestDto,
  BulkRetractRequestDto,
  ClaimDetailDto,
  ConflictViewItemDto,
  ConversationMemoryViewDto,
  CorrectClaimRequestDto,
  CurationResultDto,
  DimensionViewDto,
  ProfileSnapshotDto,
  ReadPathLensDto,
  RetractClaimRequestDto,
  UserObjectDimension,
} from '@common/synapse/introspection-types';

export interface SynapseIntrospectionRequestOptions {
  clientDemoMode?: boolean;
  subjectIndex?: number;
}

export interface SynapseSubjectUser {
  userId: string;
  email: string | null;
  displayLabel?: string;
  inCognito?: boolean;
  hasMemory: boolean;
  profileMaturity: string | null;
  activeClaimCount: number;
}

function authHeaders(accessToken?: string): HeadersInit {
  return {
    Authorization: `Bearer ${accessToken ?? ''}`,
  };
}

function withViewQuery(path: string, options?: SynapseIntrospectionRequestOptions): string {
  const params = new URLSearchParams();
  if (options?.clientDemoMode) {
    params.set('view', CLIENT_DEMO_VIEW);
  }
  if (options?.subjectIndex != null) {
    params.set('subjectIndex', String(options.subjectIndex));
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

async function synapseGet<T>(
  path: string,
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<T> {
  const token = await resolveAccessToken(accessToken);
  if (!token) {
    throw new Error('Session expired');
  }

  const response = await fetch(`${API_URL}${withViewQuery(path, options)}`, {
    headers: authHeaders(token),
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { message?: string; error?: string };
      message = body.message ?? body.error ?? message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }

  return response.json() as Promise<T>;
}

async function synapseMutate<T>(
  method: 'POST' | 'PATCH',
  path: string,
  body: unknown,
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<T> {
  const token = await resolveAccessToken(accessToken);
  if (!token) {
    throw new Error('Session expired');
  }

  const response = await fetch(`${API_URL}${withViewQuery(path, options)}`, {
    method,
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const errBody = (await response.json()) as { message?: string; error?: string };
      message = errBody.message ?? errBody.error ?? message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }

  return response.json() as Promise<T>;
}

export async function fetchSynapseUsers(
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<SynapseSubjectUser[]> {
  const result = await synapseGet<{ users: SynapseSubjectUser[] }>(
    '/admin/synapse/users',
    accessToken,
    options
  );
  return result.users ?? [];
}

export async function fetchProfileSnapshot(
  userId: string,
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<ProfileSnapshotDto> {
  return synapseGet<ProfileSnapshotDto>(
    `/admin/synapse/users/${encodeURIComponent(userId)}/snapshot`,
    accessToken,
    options
  );
}

export async function fetchDimensionView(
  userId: string,
  dimension: UserObjectDimension,
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<DimensionViewDto> {
  return synapseGet<DimensionViewDto>(
    `/admin/synapse/users/${encodeURIComponent(userId)}/dimensions/${encodeURIComponent(dimension)}`,
    accessToken,
    options
  );
}

export async function fetchClaimDetail(
  userId: string,
  claimId: string,
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<ClaimDetailDto> {
  return synapseGet<ClaimDetailDto>(
    `/admin/synapse/users/${encodeURIComponent(userId)}/claims/${encodeURIComponent(claimId)}`,
    accessToken,
    options
  );
}

export async function fetchConflicts(
  userId: string,
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<{ conflicts: ConflictViewItemDto[] }> {
  return synapseGet<{ conflicts: ConflictViewItemDto[] }>(
    `/admin/synapse/users/${encodeURIComponent(userId)}/conflicts`,
    accessToken,
    options
  );
}

export type SynapseSessionSummary = {
  chatId: string;
  title: string;
  exchangeCount: number;
  updatedAt?: string;
};

export async function fetchSynapseSessions(
  userId: string,
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<{ sessions: SynapseSessionSummary[] }> {
  return synapseGet<{ sessions: SynapseSessionSummary[] }>(
    `/admin/synapse/users/${encodeURIComponent(userId)}/sessions`,
    accessToken,
    options
  );
}

export async function fetchConversationMemoryView(
  userId: string,
  chatId: string,
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<ConversationMemoryViewDto> {
  return synapseGet<ConversationMemoryViewDto>(
    `/admin/synapse/users/${encodeURIComponent(userId)}/sessions/${encodeURIComponent(chatId)}`,
    accessToken,
    options
  );
}

export async function fetchReadPathLens(
  userId: string,
  chatId: string,
  turnId: string,
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<ReadPathLensDto> {
  return synapseGet<ReadPathLensDto>(
    `/admin/synapse/users/${encodeURIComponent(userId)}/sessions/${encodeURIComponent(chatId)}/turns/${encodeURIComponent(turnId)}/read-path`,
    accessToken,
    options
  );
}

export async function retractClaim(
  userId: string,
  claimId: string,
  request: RetractClaimRequestDto,
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<void> {
  await synapseMutate(
    'POST',
    `/admin/synapse/users/${encodeURIComponent(userId)}/claims/${encodeURIComponent(claimId)}/retract`,
    request,
    accessToken,
    options
  );
}

export async function restoreClaim(
  userId: string,
  claimId: string,
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<void> {
  await synapseMutate(
    'POST',
    `/admin/synapse/users/${encodeURIComponent(userId)}/claims/${encodeURIComponent(claimId)}/restore`,
    {},
    accessToken,
    options
  );
}

export async function correctClaim(
  userId: string,
  claimId: string,
  request: CorrectClaimRequestDto,
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<void> {
  await synapseMutate(
    'PATCH',
    `/admin/synapse/users/${encodeURIComponent(userId)}/claims/${encodeURIComponent(claimId)}`,
    request,
    accessToken,
    options
  );
}

export async function addManualClaim(
  userId: string,
  request: AddManualClaimRequestDto,
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<{ claim: { id: string } }> {
  return synapseMutate(
    'POST',
    `/admin/synapse/users/${encodeURIComponent(userId)}/claims`,
    request,
    accessToken,
    options
  );
}

export async function bulkRetractClaims(
  userId: string,
  request: BulkRetractRequestDto,
  accessToken?: string,
  options?: SynapseIntrospectionRequestOptions
): Promise<{ results: CurationResultDto[] }> {
  return synapseMutate(
    'POST',
    `/admin/synapse/users/${encodeURIComponent(userId)}/claims/bulk-retract`,
    request,
    accessToken,
    options
  );
}
